import React from 'react';
import {Box, Button, Flex, Tooltip} from '@chakra-ui/react';
import {AiFieldDefinition} from '../../../shared/fields/enrichment';
import {
  useGetAnalysisRunCredits,
  useLatestFieldDefinitionAnalysisRun,
  useUpdateFieldDefinitionAnalysisRun,
} from '../../../hooks/api/fieldDefinitionAnalysisRun';
import {useIsAnalysisRunInProgress} from './hooks/useIsAnalysisRunInProgress';
import _ from 'lodash';
import {useIsKeyplayAdmin} from '../../../hooks/api/user';
import {pluralize} from '../../../lib/helpers';
import {useCustomer} from '../../../hooks/api/metadata';

export const AnalysisRunRefreshBanner = ({
  fieldDefinition,
}: {
  fieldDefinition: AiFieldDefinition;
}) => {
  const {data: analysisRun, isPending: isAnalysisRunPending} =
    useLatestFieldDefinitionAnalysisRun({
      fieldDefinitionId: fieldDefinition.id,
    });
  const updateFieldDefinitionAnalysisRun =
    useUpdateFieldDefinitionAnalysisRun();
  const isKeyplayAdmin = useIsKeyplayAdmin();
  const customer = useCustomer();

  const isAnalysisRunInProgress = useIsAnalysisRunInProgress({
    fieldDefinitionId: fieldDefinition.id,
  });

  const analysisRunTestLists =
    analysisRun?.results.map((result) => result.testListId) ?? [];
  const {data: numCredits, isFetching: isCreditsFetching} =
    useGetAnalysisRunCredits({
      fieldDefinitionId: fieldDefinition.id,
      testListIds: analysisRunTestLists,
    });

  const hasEnoughCredits =
    isKeyplayAdmin || (numCredits ? customer.credits >= numCredits : true);

  if (
    isAnalysisRunInProgress ||
    !analysisRun ||
    _.isEqual(fieldDefinition.config, analysisRun.fieldDefinition.config)
  ) {
    return null;
  }

  const onRefreshAnalysisRun = () => {
    updateFieldDefinitionAnalysisRun.mutate({
      fieldDefinitionId: fieldDefinition.id,
      testListIds: analysisRunTestLists,
      bypassCreditCheck: isKeyplayAdmin,
    });
  };

  return (
    <Flex
      alignItems="baseline"
      bgColor="kyellow.100"
      borderRadius="md"
      justifyContent="space-between"
      py="4"
      px="4"
    >
      <Box>
        The analysis results are out of date with the current field definition.
      </Box>
      <Tooltip label="Insufficient credits" isDisabled={hasEnoughCredits}>
        <Button
          isDisabled={isAnalysisRunPending || !hasEnoughCredits}
          isLoading={
            updateFieldDefinitionAnalysisRun.isPending || isCreditsFetching
          }
          size="sm"
          variant="link"
          onClick={onRefreshAnalysisRun}
        >
          Rerun analysis ({pluralize(numCredits ?? 0, 'credit', 'credits')})
        </Button>
      </Tooltip>
    </Flex>
  );
};
